import React, { useState, useEffect } from 'react';
import { PlanPitchedFields } from '@/components/PlanPitchedFields';
import { pitchFromRoof, toPitched, toPlan } from '@/utils/pitch';
import { useAuth } from '@/contexts/AuthContext';
import MultiDefectAuditPanel from '@/components/MultiDefectAuditPanel';
import { LibraryEditModal } from '@/components/AreaDefectsAccordion';

// iter298i — Same canonicaliser used in RoofAppraisalTab; small enough
// to duplicate rather than extract for one caller.
function canonicaliseRoofKey(raw) {
  const s = String(raw || '').toLowerCase();
  if (!s) return '';
  if (s.includes('modern conc')) return 'modern_concrete';
  if (s.includes('old conc'))    return 'old_concrete';
  if (s.includes('colortile') || s.includes('colourtile')) return 'color_tile';
  if (s.includes('decramastic') || s.includes('tin tile')) return 'decramastic';
  if (s.includes('clay'))        return 'clay_tile';
  if (s.includes('longrun') || s.includes('long run') || s.includes('iron')) return 'longrun_iron';
  if (s.includes('concrete'))    return 'modern_concrete';
  return '';
}

const ROOF_TYPES = ['Iron', 'Concrete', 'Tin Tile', 'Clay', 'Butynol', 'Polycarbonate', 'Asbestos', 'Shake/Shingle', 'Slate'];
const STATES = ['Excellent', 'Good', 'Acceptable', 'Average', 'Poor', 'Very Poor', 'Failed'];

export default function RoofForm({ roof, setRoof, onSave, onCancel, saveLabel, compact = false }) {
  const { api } = useAuth();
  const [dd, setDd] = useState({});
  const [cascadeBrands, setCascadeBrands] = useState(null);    // null = use flat list, [] = "type chosen but no brands"
  const [cascadeProfiles, setCascadeProfiles] = useState(null);
  // iter298i — Extra Work audit panel toggle. Kept local to the form
  // so opening/closing it doesn't disturb the parent's Save-dirty state.
  const [showExtraAudit, setShowExtraAudit] = useState(false);

  useEffect(() => {
    api.get('/dropdowns').then(r => setDd(r.data || {})).catch(() => {});
  }, [api]);

  // Cascading: when Roof Type changes, fetch the matching brand list.
  useEffect(() => {
    const t = (roof.roof_type || '').trim();
    if (!t) { setCascadeBrands(null); return; }
    api.get(`/roof-brands?roof_type=${encodeURIComponent(t)}`)
      .then(r => setCascadeBrands(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCascadeBrands(null));
  }, [api, roof.roof_type]);

  // Cascading: when Brand changes, fetch the matching profile list.
  // Iter316 · Phase 1.1 · pass roof_type so brand-name collisions
  // (Winstone under both Old Concrete & Clay Tile) don't cross-leak
  // profiles into the wrong roof_type's dropdown.
  useEffect(() => {
    const b = (roof.roof_brand || '').trim();
    const t = (roof.roof_type || '').trim();
    if (!b) { setCascadeProfiles(null); return; }
    const qs = t
      ? `brand=${encodeURIComponent(b)}&roof_type=${encodeURIComponent(t)}`
      : `brand=${encodeURIComponent(b)}`;
    api.get(`/roof-profiles?${qs}`)
      .then(r => setCascadeProfiles(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCascadeProfiles(null));
  }, [api, roof.roof_brand, roof.roof_type]);

  // Auto-update Roof m² whenever Floor m² or Roof Angle changes.
  // Formula: Roof Area = Floor Area / cos(angle). Skips when either input is
  // invalid; user can still manually override the resulting value.
  useEffect(() => {
    const f = parseFloat(roof.floor_m2);
    const a = parseFloat(roof.roof_angle);
    if (!(f > 0 && a > 0 && a < 80)) return;
    const est = Math.round((f / Math.cos((a * Math.PI) / 180)) * 100) / 100;
    if (Math.abs((parseFloat(roof.roof_m2) || 0) - est) > 0.01) {
      setRoof(r => ({ ...r, roof_m2: est }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roof.floor_m2, roof.roof_angle]);

  const u = (k, v) => setRoof(r => ({ ...r, [k]: v }));
  // Roof angle / pitch change → re-work the worked-out side of each Plan/Pitched total.
  useEffect(() => {
    const deg = pitchFromRoof(roof);
    if (!deg) return;
    setRoof(r => {
      let next = r;
      ['hip', 'barge', 'valley'].forEach(k => {
        const src = r[`${k}_length_source`];
        const val = src === 'plan' ? toPitched(Number(r[`${k}_length_plan`]) || 0, k, deg)
          : src === 'true' ? toPlan(Number(r[`${k}_length`]) || 0, k, deg) : null;
        const field = src === 'plan' ? `${k}_length` : `${k}_length_plan`;
        if (val !== null && val !== r[field]) next = { ...next, [field]: val };
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roof.roof_angle, roof.roof_pitch]);

  const descriptions = dd.roof_descriptions || ['House', 'House and Garage', 'Garage', 'Carport', 'Warehouse', 'Motel', 'Unit / Apartment', 'Business', 'Leased Building', 'Sleepout', 'Shed', 'Other'];
  // Brand list — cascade-narrowed by roof_type when possible. If the
  // cascade returns empty (typical for legacy Access import values
  // like "Mod Conc" that aren't in the canonical roof_types list)
  // we fall back to the flat brand dropdown so existing data still
  // matches its `<option>` and stays visible in the picker.
  const brands = cascadeBrands !== null && cascadeBrands.length > 0 ? cascadeBrands : (dd.roof_brands || []);
  const colours = dd.roof_colours || [];
  // Profile list — same fallback semantics as brand.
  const profiles = cascadeProfiles !== null && cascadeProfiles.length > 0 ? cascadeProfiles : (dd.roof_profiles || []);
  const pitches = dd.roof_pitches || [];
  const ages = dd.roof_ages || [];
  const states = dd.roof_states || STATES;
  const roofTypes = dd.roof_types || ROOF_TYPES;
  const conditions = dd.colour_conditions || [];
  const growths = dd.organic_growths || [];
  const maintenanceConditions = dd.maintenance_conditions || ['Clean', 'Dirty', 'Mouldy', 'Damaged', 'N/A'];
  // Iter146 — Material Type list (Iron family). Only surfaced when
  // Roof Type === 'Iron' so the form stays compact for tile/concrete
  // inspections. Operator-managed via /admin/roof-details-dropdowns.
  // Iter316 · Phase 1.1 · canonical list (matches search.py source of
  // truth): server-provided `dd.material_types` is authoritative; the
  // literal fallback here is only used if the dropdowns endpoint is
  // unreachable — keep it aligned with ROOF_TAXONOMY_GOLIVE.md so it
  // never drifts back into legacy names (Maxx / Zcore / Aluguard).
  const materialTypes = dd.material_types || ['Maxam', 'Galv', 'Zinacore', 'Zinc', 'Aluminium', 'Magnaflow', 'MagnaFlowPlus'];
  const isIronRoof = String(roof.roof_type || '').trim().toLowerCase() === 'iron';

  // When Roof Type changes, clear Brand+Profile if they no longer match
  const onTypeChange = (newType) => {
    setRoof(r => ({ ...r, roof_type: newType, roof_brand: '', roof_profile: '' }));
  };
  const onBrandChange = (newBrand) => {
    setRoof(r => ({ ...r, roof_brand: newBrand, roof_profile: '' }));
  };

  // Allow free text on Description so the user can enter a custom label
  // (Access lets them type "House and Sleepout"). We render it as a datalist.
  //
  // When the row carries a legacy short-form value (e.g. "Mod Conc"
  // for roof_type, "Galvanb" for roof_colour from the legacy Access
  // import) that isn't in the canonical option list, we prepend it
  // as a "(legacy)" option so:
  //   1. the select renders the actual stored value (instead of
  //      silently looking blank), and
  //   2. the operator can see the legacy spelling needs correcting
  //      and pick a canonical replacement.
  const Sel = ({ value, opts, onChange, placeholder, testId }) => {
    // 2026-02 Go-Live · roof-colour redesign — seed carries indented
    // brand names ("   Ebony") so operators see them as refinements of
    // the matching general colour. We render those indented in the
    // label but strip the whitespace on the stored value so filters,
    // reports and the Component engine see clean names ("Ebony"). All
    // non-colour dropdowns are untouched (trim() is a no-op on them).
    const cleanValue = (o) => (typeof o === 'string' ? o.trim() : o);
    const inList = !value || (opts || []).some(o => cleanValue(o) === value);
    return (
      <select data-testid={testId} value={value || ''} onChange={e => onChange(e.target.value)} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm bg-white">
        <option value="">{placeholder || 'Select...'}</option>
        {!inList && (
          <option value={value} className="text-amber-700 italic">{value}  (legacy — pick canonical)</option>
        )}
        {opts.map(o => <option key={o} value={cleanValue(o)}>{o}</option>)}
      </select>
    );
  };

  const numField = (label, key, opts = {}) => (
    <div>
      <label className="text-xs text-slate-500">{label}</label>
      <input
        data-testid={`roof-${key.replace(/_/g, '-')}`}
        type="number"
        min={opts.min}
        max={opts.max}
        step={opts.step ?? 'any'}
        value={roof[key] ?? 0}
        onChange={e => u(key, e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
        className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm font-mono-num"
      />
    </div>
  );

  if (compact) {
    return (
      <div data-testid="roof-form-compact" className="border border-[#D35400] rounded-lg p-3 bg-orange-50/30 space-y-2">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <div><label className="text-xs text-slate-500">Description</label>
            <Sel value={roof.description} opts={descriptions} onChange={v => u('description', v)} placeholder="House…" testId="roof-description-select" /></div>
          <div><label className="text-xs text-slate-500">Roof Type</label>
            <Sel value={roof.roof_type} opts={roofTypes} onChange={v => u('roof_type', v)} testId="roof-type-select" /></div>
          <div><label className="text-xs text-slate-500">Condition</label>
            <Sel value={roof.roof_state} opts={states} onChange={v => u('roof_state', v)} testId="roof-state-select" /></div>
          {numField('Roof m2', 'roof_m2')}
        </div>
        <div className="flex gap-2">
          <button data-testid="save-roof-btn" onClick={onSave} className="px-3 py-1 bg-[#D35400] text-white rounded text-sm font-medium hover:bg-[#B84500]">{saveLabel}</button>
          <button onClick={onCancel} className="px-3 py-1 border border-[#E2E2DF] rounded text-sm hover:bg-slate-50">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="roof-form" className="border border-[#D35400] rounded-lg p-4 bg-orange-50/30 mb-3 space-y-4">
      {/* Identity row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><label className="text-xs text-slate-500">Description</label>
          <Sel value={roof.description} opts={descriptions} onChange={v => u('description', v)} placeholder="House…" testId="roof-description-select" /></div>
        <div><label className="text-xs text-slate-500">Roof Type</label>
          <Sel value={roof.roof_type} opts={roofTypes} onChange={onTypeChange} testId="roof-type-select" /></div>
        <div>
          <label className="text-xs text-slate-500 flex items-center justify-between">
            <span>Brand</span>
            {roof.roof_type && cascadeBrands !== null && <span className="text-[9px] text-slate-400 font-normal">{cascadeBrands.length} for {roof.roof_type}</span>}
          </label>
          <Sel value={roof.roof_brand} opts={brands} onChange={onBrandChange} testId="roof-brand-select" placeholder={roof.roof_type ? 'Pick brand…' : 'Select roof type first'} /></div>
        <div>
          <label className="text-xs text-slate-500 flex items-center justify-between">
            <span>Profile</span>
            {roof.roof_brand && cascadeProfiles !== null && <span className="text-[9px] text-slate-400 font-normal">{cascadeProfiles.length} for {roof.roof_brand}</span>}
          </label>
          <Sel value={roof.roof_profile} opts={profiles} onChange={v => u('roof_profile', v)} testId="roof-profile-select" placeholder={roof.roof_brand ? 'Pick profile…' : 'Select brand first'} /></div>
        {/* Iter146 — Material Type (Iron only). 4th cascade tier; drives
            the Components-tab Iron-section narrowing (Sheet / Ridge /
            Valley / Barge / Flashing pre-select rate rows that match).
            Stays in the same 4-col row by wrapping the Profile div when
            Iron is selected. */}
        {isIronRoof && (
          <div>
            <label className="text-xs text-slate-500 flex items-center justify-between">
              <span>Material Type</span>
              <span className="text-[9px] text-amber-600 font-normal">Iron</span>
            </label>
            <Sel
              value={roof.material_type || ''}
              opts={materialTypes}
              onChange={v => u('material_type', v)}
              testId="roof-material-type-select"
              placeholder="Pick material…"
            /></div>
        )}
      </div>

      {/* Geometry row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><label className="text-xs text-slate-500">Pitch</label>
          <Sel value={roof.roof_pitch} opts={pitches} onChange={v => u('roof_pitch', v)} testId="roof-pitch-select" /></div>
        {numField('Roof angle (°)', 'roof_angle', { min: 0, max: 90, step: 0.5 })}
        <div><label className="text-xs text-slate-500">Stories</label>
          <input data-testid="roof-stories" type="number" min="0" max="10" value={roof.roof_stories ?? 1} onChange={e => u('roof_stories', parseInt(e.target.value) || 0)} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm font-mono-num" /></div>
        <div><label className="text-xs text-slate-500">Colour</label>
          <Sel value={roof.roof_colour} opts={colours} onChange={v => u('roof_colour', v)} testId="roof-colour-select" /></div>
      </div>

      {/* Surface condition row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><label className="text-xs text-slate-500">Surface</label>
          <Sel value={roof.colour_condition} opts={conditions} onChange={v => u('colour_condition', v)} testId="roof-surface-select" /></div>
        <div><label className="text-xs text-slate-500">Growth</label>
          <Sel value={roof.organic_growth} opts={growths} onChange={v => u('organic_growth', v)} testId="roof-growth-select" /></div>
        <div><label className="text-xs text-slate-500">Age</label>
          <Sel value={roof.roof_age} opts={ages} onChange={v => u('roof_age', v)} testId="roof-age-select" /></div>
        <div><label className="text-xs text-slate-500">State</label>
          <Sel value={roof.roof_state} opts={states} onChange={v => u('roof_state', v)} testId="roof-state-select" /></div>
      </div>

      {/* Measurements row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {numField('Floor m²', 'floor_m2')}
        <div>
          <label className="text-xs text-slate-500 flex items-center justify-between">
            <span>Roof m²</span>
            {(() => {
              const f = parseFloat(roof.floor_m2);
              const a = parseFloat(roof.roof_angle);
              if (f > 0 && a > 0 && a < 80) {
                return (
                  <span title={`Auto-calculated: ${f} m² ÷ cos(${a}°)`} className="text-[10px] font-semibold text-[#D35400]">auto from floor + angle</span>
                );
              }
              return null;
            })()}
          </label>
          <input
            data-testid="roof-roof-m2"
            type="number"
            step="any"
            value={roof.roof_m2 ?? 0}
            onChange={e => u('roof_m2', e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
            className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm font-mono-num"
          />
        </div>
        {numField('Ridge length (m)', 'ridge_length')}
      </div>

      {/* iter303ak-fix54 — Universal roof-edge measurements. Perimeter,
          Barge and Valley are now shown for every roof (were previously
          ColourTile / Decramastic-only). Perimeter + Barge still drive
          nail-replacement quantities for those brands; Valley drives
          valley-flashing quantity across the board. */}
      <div className="border-t border-[#E2E2DF] pt-3">
        <label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 block mb-2">
          Roof edge measurements (m)
        </label>
        <div className="grid grid-cols-3 gap-3">
          {numField('Perimeter length (m)', 'perimeter_length')}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3" data-testid="roof-plan-pitched-totals">
          {[['hip', 'Hip length'], ['barge', 'Barge length'], ['valley', 'Valley length']].map(([k, lbl]) => (
            <div key={k}>
              <div className="text-xs text-slate-500 mb-1">{lbl}</div>
              <PlanPitchedFields
                kind={k} pitchDeg={pitchFromRoof(roof)} compact testId={`roof-${k}-length`}
                plan={roof[`${k}_length_plan`]} pitched={roof[`${k}_length`]} source={roof[`${k}_length_source`]}
                onChange={({ plan, pitched, source }) => setRoof(r => ({
                  ...r, [`${k}_length_plan`]: plan, [`${k}_length`]: pitched, [`${k}_length_source`]: source,
                }))}
              />
            </div>
          ))}
        </div>
        {(() => {
          const brand = (roof.roof_brand || '').toLowerCase();
          const isColorOrDecra = brand.includes('colortile') || brand.includes('colourtile') || brand.includes('decramastic');
          if (!isColorOrDecra) return (
            <div className="text-[10px] text-slate-500 italic mt-1.5">
              Perimeter + Barge feed spouting / edge-flashing quantities · Valley drives valley-flashing metres.
            </div>
          );
          return (
            <div className="text-[10px] text-slate-500 italic mt-1.5">
              ColourTile / Decramastic: Perimeter drives bottom-course nail replacement · Barge drives barge nail replacement · Valley drives valley-flashing metres.
            </div>
          );
        })()}
      </div>

      {/* iter303ak-fix11 · House Maintenance sub-form. Skeleton for
          future cascade triggers → House Wash / Drive Wash / Treat /
          Repaint / Repair. Gated by include_house_maintenance so
          operators can skip the section if the client isn't interested. */}
      <div className="border-t border-[#E2E2DF] pt-3">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
            House Maintenance <span className="text-[10px] font-normal normal-case tracking-normal text-slate-400">(optional — enable to appraise house / drive / paths / fences)</span>
          </label>
          <label className="flex items-center gap-2 text-xs font-medium text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="roof-include-house-maintenance"
              checked={!!roof.include_house_maintenance}
              onChange={e => u('include_house_maintenance', e.target.checked)}
              className="w-4 h-4 accent-[#D35400]"
            />
            Include
          </label>
        </div>
        {roof.include_house_maintenance && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><label className="text-xs text-slate-500">House</label>
              <Sel value={roof.house_condition} opts={maintenanceConditions} onChange={v => u('house_condition', v)} testId="roof-house-condition-select" /></div>
            <div><label className="text-xs text-slate-500">Driveway</label>
              <Sel value={roof.driveway_condition} opts={maintenanceConditions} onChange={v => u('driveway_condition', v)} testId="roof-driveway-condition-select" /></div>
            <div><label className="text-xs text-slate-500">Paths</label>
              <Sel value={roof.paths_condition} opts={maintenanceConditions} onChange={v => u('paths_condition', v)} testId="roof-paths-condition-select" /></div>
            <div><label className="text-xs text-slate-500">Fences</label>
              <Sel value={roof.fences_condition} opts={maintenanceConditions} onChange={v => u('fences_condition', v)} testId="roof-fences-condition-select" /></div>
          </div>
        )}
      </div>

      {/* Extra work row */}
      <div className="border-t border-[#E2E2DF] pt-3">
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Extra work</label>
          <button
            type="button"
            onClick={() => setShowExtraAudit(v => !v)}
            className={`flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-md border transition-colors ${showExtraAudit ? 'bg-purple-600 text-white border-purple-700' : 'text-purple-700 border-purple-400 hover:bg-purple-50'}`}
            data-testid="roof-extra-work-audit-toggle"
            title="View / edit / sort the Extra Work library"
          >
            📚 {showExtraAudit ? 'Hide' : 'View all'} extra-work scripts
          </button>
        </div>
        <div className={showExtraAudit ? 'grid grid-cols-1 xl:grid-cols-2 gap-3' : ''}>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-3">
            <div>
              <label className="text-xs text-slate-500">Comments</label>
              <textarea data-testid="roof-extra-work-comments" rows={2} value={roof.extra_work_comments || ''} onChange={e => u('extra_work_comments', e.target.value)} placeholder="e.g. Replace 3 backtrays, ridge re-bed…" className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Estimate ($)</label>
              <input data-testid="roof-extra-work-estimate" type="number" min="0" step="0.01" value={roof.extra_work_estimate ?? 0} onChange={e => u('extra_work_estimate', parseFloat(e.target.value) || 0)} className="w-full px-2 py-1.5 border border-[#E2E2DF] rounded text-sm font-mono-num" />
            </div>
          </div>
          {showExtraAudit && (
            <MultiDefectAuditPanel
              area={{ key: 'extra_work_comment', label: 'Extra Work' }}
              api={api}
              currentRoofKey={canonicaliseRoofKey(roof.roof_type)}
              onScriptsChanged={() => {}}
              LibraryEditModal={LibraryEditModal}
            />
          )}
        </div>
      </div>

      <div className="flex gap-2">
        <button data-testid="save-roof-btn" onClick={onSave} className="px-3 py-1.5 bg-[#D35400] text-white rounded text-sm font-medium hover:bg-[#B84500]">{saveLabel}</button>
        <button onClick={onCancel} className="px-3 py-1.5 border border-[#E2E2DF] rounded text-sm hover:bg-slate-50">Cancel</button>
      </div>
    </div>
  );
}
